import type { ExperimentUser } from "@amplitude/experiment-js-client"

import { removeEmptyValues } from "@bounce/util"
import type { Nullable } from "@bounce/util"

import { initialize } from "./experiment/experiment"
import type { UniversalExperimentClient } from "./experiment/types"
import { clearExposureMemory, exposeOnce } from "./exposure"
import { getVariantFromFallback } from "./helpers/getVariantFromFallback"
import type { Fallback, FeatureFlagsAppConfig, Variant } from "./types"

type UserValues = {
  anonymousId: string
  realUserId: Nullable<string>
  targetingAttributes?: UserAttributes
}

type InitOptions = FeatureFlagsAppConfig["featureFlags"] & { user: UserValues }

export type UserAttributes = {
  [property: string]: Nullable<string | number | boolean>
  [index: number]: never
}

export type UpdateUserOptions = UserValues

export type GetVariantResult<TPayload> = {
  expKey: string | undefined
  value: string
  key: string | undefined
  // If the user receives "off" as the value, the payload will be undefined
  payload: TPayload | undefined
}

export type FeatureFlagClient = {
  init: (options: InitOptions) => Promise<void>
  getVariant: <TPayload>(name: string, fallbackVariant: Fallback<TPayload>) => Variant<TPayload>
  getAllVariants: <TVariants extends Record<string, Variant> = Record<string, Variant>>() => Partial<TVariants>
  updateUser: (user: UpdateUserOptions) => Promise<void>
}

type GetServerVariantOptions = {
  name: string
  user: ExperimentUser
}

export type ExperimentVariant = {
  value: string
  key: string
  payload?: {
    pageTypes?: string[]
    type?: string
    translations?: Record<string, string>
  }
}
export type AllVariants = Record<string, ExperimentVariant>

export type FeatureFlagServerClient = {
  init: (options: Omit<InitOptions, "user">) => void
  getVariant: <TPayload>(options: GetServerVariantOptions) => Promise<Partial<GetVariantResult<TPayload>>>
  getAllVariants: <TVariants extends Record<string, ExperimentVariant> = AllVariants>(
    user: ExperimentUser,
  ) => Promise<TVariants>
}

let experimentClient: UniversalExperimentClient | undefined

const mapUserValuesToExperimentUser = (user: UserValues): ExperimentUser => ({
  device_id: user.anonymousId,
  user_id: user.realUserId ?? undefined,
  user_properties: removeEmptyValues(user.targetingAttributes ?? {}),
})

export const Client: FeatureFlagClient = {
  init: async ({ sdkKey, isAutoExposureTrackingEnabled, user }) => {
    experimentClient = initialize(sdkKey, {
      exposureTrackingProvider: {
        track: exposure => {
          if (!isAutoExposureTrackingEnabled) return
          exposeOnce(exposure)
        },
      },
      retryFetchOnFailure: true,
      flagsServerUrl: typeof window !== "undefined" ? "https://flag.amp.bounce.com" : undefined,
      serverUrl: typeof window !== "undefined" ? "https://api.amp.bounce.com" : undefined,
    })

    const experimentUser = mapUserValuesToExperimentUser(user)
    await experimentClient.start(experimentUser)
    clearExposureMemory()
  },

  getVariant<TPayload>(name: string, fallback: Fallback<TPayload>): GetVariantResult<TPayload> {
    const defaultVariant = getVariantFromFallback(fallback)

    const {
      value = defaultVariant.value,
      payload,
      expKey,
      key,
    } = experimentClient?.variant(name, fallback) ?? defaultVariant

    return { value, payload, expKey, key }
  },

  getAllVariants<TVariants extends Record<string, Variant> = Record<string, Variant>>() {
    return (experimentClient?.all() ?? {}) as Partial<TVariants>
  },

  updateUser: async user => {
    if (!experimentClient) return
    const updatedUser = mapUserValuesToExperimentUser(user)

    experimentClient.setUser(updatedUser)
    await experimentClient
      .fetch(updatedUser)
      .then(() => {
        clearExposureMemory()
      })
      .catch(() => {})
  },
}

export const ServerClient: FeatureFlagServerClient = {
  init: ({ sdkKey }) => {
    experimentClient = initialize(sdkKey, {
      automaticExposureTracking: false,
      pollOnStart: false,
      fetchTimeoutMillis: 2000,
      retryFetchOnFailure: false,
    })
  },

  async getVariant({ name, user }) {
    if (!experimentClient) throw new Error("Feature flag server client is not initialized")

    return experimentClient
      .fetch(user)
      .then(client => client.variant(name))
      .catch(() => ({
        value: undefined,
        payload: undefined,
      }))
  },
  async getAllVariants<TVariants extends Record<string, ExperimentVariant> = AllVariants>(user: ExperimentUser) {
    if (!experimentClient) throw new Error("Feature flag server client is not initialized")

    try {
      const client = await experimentClient.fetch(user)
      return client.all() as TVariants
    } catch {
      return {} as TVariants
    }
  },
}
